/**
 * CL-391A / CL-391B — Controlled project document library (pmProjectFiles metadata + pmProjects sections).
 */

import {
  filterHistoricalArchiveClientFiles,
  isHistoricalArchiveDocument,
  sortHistoricalArchiveFiles,
} from "./pmProjectLifecycle";

export const CONTROLLED_DOCUMENT_LIBRARY_TYPE = "controlled_document";

export const CONTROLLED_DOCUMENT_ORPHAN_SECTION_HISTORICAL = Object.freeze({
  id: "__general_documents",
  title: "General Documents",
});

export const CONTROLLED_DOCUMENT_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const CONTROLLED_SECTION_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const CONTROLLED_DOCUMENT_EMPTY_CLIENT =
  "No project documents have been shared yet.";

export const CONTROLLED_DOCUMENT_EMPTY_ADMIN =
  "No controlled document sections yet. Add a section to organize permits, site documents, engineering, and closeout files.";

export const CONTROLLED_DOCUMENT_EMPTY_HISTORICAL_CLIENT =
  "No archive files have been shared yet. Mark can add drawings, permit packages, photos, or project records.";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function isControlledProjectDocument(file) {
  return safeStr(file?.libraryType).toLowerCase() === CONTROLLED_DOCUMENT_LIBRARY_TYPE;
}

export function isHistoricalOrDeletedFile(file) {
  if (safeStr(file?.status).toLowerCase() === "deleted") return true;
  if (file?.isHistoricalArchiveDocument === true) return true;
  return false;
}

export function normalizeControlledDocumentSections(project) {
  const raw = Array.isArray(project?.controlledDocumentSections)
    ? project.controlledDocumentSections
    : [];
  return raw
    .map((row, index) => {
      const id = safeStr(row?.id);
      if (!id) return null;
      return {
        id,
        title: safeStr(row?.title) || "Untitled section",
        description: safeStr(row?.description) || null,
        order: Number.isFinite(Number(row?.order)) ? Number(row.order) : index,
        status: safeStr(row?.status).toLowerCase() || CONTROLLED_SECTION_STATUS.ACTIVE,
      };
    })
    .filter(Boolean);
}

export function sortControlledSections(sections = []) {
  return [...sections].sort((a, b) => {
    const ao = Number(a.order) || 0;
    const bo = Number(b.order) || 0;
    if (ao !== bo) return ao - bo;
    return safeStr(a.title).localeCompare(safeStr(b.title));
  });
}

export function isActiveControlledSection(section) {
  return safeStr(section?.status).toLowerCase() !== CONTROLLED_SECTION_STATUS.ARCHIVED;
}

export function isActiveControlledDocument(file) {
  if (!isControlledProjectDocument(file)) return false;
  if (isHistoricalOrDeletedFile(file)) return false;
  return safeStr(file?.documentStatus).toLowerCase() !== CONTROLLED_DOCUMENT_STATUS.ARCHIVED;
}

export function isClientVisibleControlledDocument(file, section = null) {
  if (!isActiveControlledDocument(file)) return false;
  if (file?.clientVisible === false) return false;
  if (section && !isActiveControlledSection(section)) return false;
  return true;
}

export function getControlledDocumentDisplayTitle(file) {
  return (
    safeStr(file?.documentDisplayTitle) ||
    safeStr(file?.historicalArchiveTitle) ||
    safeStr(file?.name) ||
    "Document"
  );
}

export function isHistoricalArchiveLibraryDocument(file, project) {
  if (!file) return false;
  if (safeStr(file?.status).toLowerCase() === "deleted") return false;
  if (isControlledProjectDocument(file)) return false;
  return isHistoricalArchiveDocument(file, project);
}

export function isActiveHistoricalArchiveLibraryDocument(file, project) {
  if (!isHistoricalArchiveLibraryDocument(file, project)) return false;
  return safeStr(file?.documentStatus).toLowerCase() !== CONTROLLED_DOCUMENT_STATUS.ARCHIVED;
}

export function isClientVisibleHistoricalArchiveLibraryDocument(file, project, section = null) {
  if (!isActiveHistoricalArchiveLibraryDocument(file, project)) return false;
  if (file?.clientVisible === false) return false;
  if (section && !isActiveControlledSection(section)) return false;
  return true;
}

export function getControlledDocumentSectionTitle(file, sectionsById = new Map()) {
  const fromFile = safeStr(file?.documentSectionTitle);
  if (fromFile) return fromFile;
  const section = sectionsById.get(safeStr(file?.documentSectionId));
  return section?.title || "General";
}

/**
 * @param {object[]} files
 * @param {object[]} sections
 */
export function filterControlledDocumentsForAdmin(files = [], sections = []) {
  const sectionIds = new Set(sections.map((s) => s.id));
  return (files || []).filter((file) => {
    if (!isControlledProjectDocument(file) || isHistoricalOrDeletedFile(file)) return false;
    const sid = safeStr(file.documentSectionId);
    return !sid || sectionIds.has(sid) || sections.length === 0;
  });
}

/**
 * @param {object[]} files
 * @param {object[]} sections
 */
export function filterControlledDocumentsForClient(files = [], sections = []) {
  const activeSections = sortControlledSections(sections).filter(isActiveControlledSection);
  const sectionsById = new Map(activeSections.map((s) => [s.id, s]));
  return (files || []).filter((file) => {
    const sid = safeStr(file.documentSectionId);
    const section = sectionsById.get(sid);
    if (!section) return false;
    return isClientVisibleControlledDocument(file, section);
  });
}

function documentSortKey(file) {
  const title = getControlledDocumentDisplayTitle(file).toLowerCase();
  return title;
}

/**
 * @param {object[]} files — already filtered for audience
 * @param {object[]} sections
 */
/**
 * @param {object} [options]
 * @param {string} [options.orphanSectionId]
 * @param {string} [options.orphanSectionTitle]
 * @param {(a: object, b: object) => number} [options.sortOrphans]
 */
export function groupControlledDocumentsBySection(files = [], sections = [], options = {}) {
  const orphanSectionId = safeStr(options.orphanSectionId) || "__unsectioned";
  const orphanSectionTitle = safeStr(options.orphanSectionTitle) || "Unsectioned";
  const sortOrphans =
    typeof options.sortOrphans === "function"
      ? options.sortOrphans
      : (a, b) => documentSortKey(a).localeCompare(documentSortKey(b));

  const sortedSections = sortControlledSections(sections);
  const groups = new Map();

  for (const section of sortedSections) {
    groups.set(section.id, { section, documents: [] });
  }

  const orphans = [];
  for (const file of files || []) {
    const sid = safeStr(file.documentSectionId);
    if (sid && groups.has(sid)) {
      groups.get(sid).documents.push(file);
    } else {
      orphans.push(file);
    }
  }

  const result = [];
  for (const section of sortedSections) {
    const bucket = groups.get(section.id);
    if (!bucket) continue;
    bucket.documents.sort((a, b) => documentSortKey(a).localeCompare(documentSortKey(b)));
    result.push(bucket);
  }

  if (orphans.length) {
    orphans.sort(sortOrphans);
    result.push({
      section: {
        id: orphanSectionId,
        title: orphanSectionTitle,
        description: null,
        order: 9999,
        status: CONTROLLED_SECTION_STATUS.ACTIVE,
      },
      documents: orphans,
    });
  }

  return result;
}

/**
 * @param {object[]} files
 * @param {object} project
 * @param {object[]} sections
 */
export function filterHistoricalArchiveLibraryForAdmin(files = [], project, sections = []) {
  return filterHistoricalArchiveClientFiles(files, project).filter((file) => {
    if (safeStr(file?.status).toLowerCase() === "deleted") return false;
    if (isControlledProjectDocument(file)) return false;
    if (safeStr(file?.libraryType).toLowerCase() === "client_document") return false;
    return true;
  });
}

/**
 * @param {object[]} files
 * @param {object} project
 * @param {object[]} sections
 */
export function filterHistoricalArchiveLibraryForClient(files = [], project, sections = []) {
  const archiveFiles = filterHistoricalArchiveClientFiles(files, project).filter((file) => {
    if (isControlledProjectDocument(file)) return false;
    if (safeStr(file?.libraryType).toLowerCase() === "client_document") return false;
    return true;
  });
  const activeSections = sortControlledSections(sections).filter(isActiveControlledSection);
  const sectionsById = new Map(activeSections.map((s) => [s.id, s]));

  return archiveFiles.filter((file) => {
    if (safeStr(file?.status).toLowerCase() === "deleted") return false;
    if (safeStr(file?.documentStatus).toLowerCase() === CONTROLLED_DOCUMENT_STATUS.ARCHIVED) return false;
    if (file?.clientVisible === false) return false;
    const sid = safeStr(file.documentSectionId);
    if (!sid) return true;
    const section = sectionsById.get(sid);
    if (!section) return false;
    return isActiveControlledSection(section);
  });
}

/**
 * @param {object[]} files — client-filtered historical archive library files
 * @param {object[]} sections
 * @param {object} [project] — optional, for orphan sort order
 */
export function groupHistoricalArchiveLibraryBySection(files = [], sections = [], project = null) {
  let sortOrphans = (a, b) => documentSortKey(a).localeCompare(documentSortKey(b));
  if (project) {
    const ordered = sortHistoricalArchiveFiles(files, project);
    const orderIndex = new Map(ordered.map((f, i) => [safeStr(f.id), i]));
    sortOrphans = (a, b) => {
      const ai = orderIndex.has(safeStr(a.id)) ? orderIndex.get(safeStr(a.id)) : 99999;
      const bi = orderIndex.has(safeStr(b.id)) ? orderIndex.get(safeStr(b.id)) : 99999;
      if (ai !== bi) return ai - bi;
      return documentSortKey(a).localeCompare(documentSortKey(b));
    };
  }

  return groupControlledDocumentsBySection(files, sections, {
    orphanSectionId: CONTROLLED_DOCUMENT_ORPHAN_SECTION_HISTORICAL.id,
    orphanSectionTitle: CONTROLLED_DOCUMENT_ORPHAN_SECTION_HISTORICAL.title,
    sortOrphans,
  });
}

export function createControlledSectionDraft({ title, description = "", order = 0 } = {}) {
  const cleanTitle = safeStr(title);
  if (!cleanTitle) throw new Error("Section title is required.");
  const slug = cleanTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
  return {
    id: `sec_${slug || "section"}_${Date.now()}`,
    title: cleanTitle,
    description: safeStr(description) || null,
    order: Number.isFinite(Number(order)) ? Number(order) : 0,
    status: CONTROLLED_SECTION_STATUS.ACTIVE,
  };
}

export function buildControlledDocumentFirestorePatch({
  sectionId,
  sectionTitle,
  displayTitle,
  description,
  clientVisible = true,
  documentStatus = CONTROLLED_DOCUMENT_STATUS.ACTIVE,
} = {}) {
  return {
    libraryType: CONTROLLED_DOCUMENT_LIBRARY_TYPE,
    documentSectionId: safeStr(sectionId) || null,
    documentSectionTitle: safeStr(sectionTitle) || null,
    documentDisplayTitle: safeStr(displayTitle) || null,
    documentDescription: safeStr(description) || null,
    clientVisible: clientVisible !== false,
    documentStatus: safeStr(documentStatus).toLowerCase() || CONTROLLED_DOCUMENT_STATUS.ACTIVE,
  };
}

/** CL-391B — section metadata for historical archive documents (no libraryType change). */
export function buildHistoricalArchiveDocumentSectionPatch({
  sectionId,
  sectionTitle,
  displayTitle,
  description,
  clientVisible = true,
  documentStatus = CONTROLLED_DOCUMENT_STATUS.ACTIVE,
} = {}) {
  const title = safeStr(displayTitle) || null;
  const desc = safeStr(description) || null;
  return {
    documentSectionId: safeStr(sectionId) || null,
    documentSectionTitle: safeStr(sectionTitle) || null,
    documentDisplayTitle: title,
    documentDescription: desc,
    historicalArchiveTitle: title,
    historicalArchiveDescription: desc,
    clientVisible: clientVisible !== false,
    documentStatus: safeStr(documentStatus).toLowerCase() || CONTROLLED_DOCUMENT_STATUS.ACTIVE,
  };
}
